import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { projectServiceEndpoint } from "api/project";

import type { LineStyle } from "components/project/shapefiles/shapefiles.constants";

import { PROJECT_SHAPEFILE_FEATURES_QUERY_KEYS } from "../../queries/useProjectShapefileFeaturesQuery";
import { updateShapefilePropertiesCache } from "../utils/updateShapefilePropertiesCache";

export interface IUseUpdateProjectGeomMutationParams {
  onError?: (error) => void;
  onMutate?: (variables: IUpdateProjectGeomParamsT) => void;
  onSettled?: () => void;
  onSuccess?: (data) => void;
}
export interface IUpdateProjectGeomParamsT {
  shapeFileGeomId: string;
  body: {
    color?: string;
    thickness?: number;
    strokeColor?: string;
    lineStyle?: LineStyle;
    fillPattern?: string;
  };
}

async function UpdateProjectGeom(params: IUpdateProjectGeomParamsT) {
  const url = `${projectServiceEndpoint}/shapefile-geom/update/${params.shapeFileGeomId}`;
  const response = await axios.put(url, params.body);
  return response.data;
}

export function useUpdateProjectGeomMutation({
  onError,
  onMutate,
  onSettled,
  onSuccess
}: IUseUpdateProjectGeomMutationParams) {
  const queryClient = useQueryClient();

  return useMutation(
    (params: IUpdateProjectGeomParamsT) => UpdateProjectGeom({ ...params }),
    {
      onMutate: async (variables) => {
        // Call user's onMutate if provided
        onMutate && onMutate(variables);
      },
      onSuccess: (data, variables) => {
        updateShapefilePropertiesCache({
          queryClient,
          shapefileGeomIds: [variables.shapeFileGeomId],
          update: variables.body
        });

        // Always invalidate the detail view query (has full geometry)
        queryClient.invalidateQueries({
          queryKey: [PROJECT_SHAPEFILE_FEATURES_QUERY_KEYS.projectShapefileFeatures]
        });

        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
